import React from 'react';
import { useTranslation } from 'react-i18next';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Brain, CircleCheck, ImageOff } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { LoadingButton } from '../../common/LoadingButton';
import { cn } from '@/lib/utils';
import type { DocumentImage } from '@hooks/useDocumentImages';

interface ExtractedImageCardProps {
  image: DocumentImage;
  blobUrl?: string;
  failed: boolean;
  isSelected: boolean;
  selectionIndex: number;
  analyzing: boolean;
  onToggleSelection: (imageId: string) => void;
  onAnalyze: (imageId: string) => void;
}

const ExtractedImageCard: React.FC<ExtractedImageCardProps> = ({
  image,
  blobUrl,
  failed,
  isSelected,
  selectionIndex,
  analyzing,
  onToggleSelection,
  onAnalyze,
}) => {
  const { t } = useTranslation();
  const selectId = `image-select-${image.id}`;
  const caption = { page: image.pageNumber, index: image.imageIndex + 1 };

  return (
    // No selected-state tint: `primary.lighter` was never a theme token, so the
    // 2px border has always been the only selection cue.
    <div
      className={cn(
        'flex flex-col rounded-xl bg-card p-4 text-card-foreground shadow-sm transition-all hover:shadow-md',
        isSelected ? 'border-2 border-primary' : 'border border-border',
      )}
    >
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-1">
          {/* MUI drew the same glyph for both states and told them apart by
              colour alone; a stock checkbox would lose the distinction, so the
              tinted glyph is reproduced on the Radix root directly. */}
          <CheckboxPrimitive.Root
            id={selectId}
            checked={isSelected}
            onCheckedChange={() => onToggleSelection(image.id)}
            // grid+size restores the 38px target MUI's ButtonBase padding gave
            // it; the .revy-ui reset zeroes button padding, so the hit area
            // would otherwise be just the 20px glyph.
            className="grid size-9 place-items-center rounded-full text-muted-foreground transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring data-[state=checked]:text-primary"
          >
            <CircleCheck className="size-5" strokeWidth={1.9} />
          </CheckboxPrimitive.Root>
          <Label htmlFor={selectId} className="revy-cjk cursor-pointer text-xs font-bold leading-[1.5]">
            {t('common.select')}
          </Label>
        </div>
        {isSelected && (
          <Badge className="revy-figure">{`#${selectionIndex + 1}`}</Badge>
        )}
      </div>

      {blobUrl ? (
        <button
          type="button"
          onClick={() => window.open(blobUrl, '_blank')}
          aria-label={t('documents.openFullImage', caption)}
          className="mb-2 block w-full rounded-lg focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
        >
          <img
            src={blobUrl}
            alt={t('documents.extractedImageAlt', caption)}
            className="h-[200px] w-full rounded-lg bg-muted object-contain"
            loading="lazy"
          />
        </button>
      ) : (
        <div
          className={cn(
            'mb-2 flex h-[200px] w-full flex-col items-center justify-center rounded-lg',
            failed ? 'border border-destructive/40' : 'bg-muted',
          )}
        >
          {failed ? (
            <>
              <ImageOff className="mb-2 size-10 text-destructive" strokeWidth={1.6} />
              <p className="revy-cjk text-center text-xs leading-[1.5] text-destructive">
                {t('documents.imageLoadFailed')}
              </p>
              <p className="revy-cjk text-center text-xs leading-[1.5] text-muted-foreground">
                {t('documents.imageFileNotFound')}
              </p>
            </>
          ) : (
            <Spinner size="default" />
          )}
        </div>
      )}

      <p className="revy-figure text-center text-xs font-bold">
        {t('documents.imagePageCaption', caption)}
      </p>
      <p className="revy-figure mb-2 text-center text-xs text-muted-foreground">
        {image.width} × {image.height} • {(image.fileSize / 1024).toFixed(1)} KB
      </p>

      {image.aiDescription ? (
        <div className="mt-2">
          {/* `info.lighter` is not a theme token — this panel has never had a
              background, only the border. */}
          <div className="rounded-lg border border-primary/30 p-2">
            <div className="mb-1 flex items-center gap-1">
              <Brain className="size-4 text-primary" strokeWidth={1.9} />
              <span className="revy-cjk text-xs font-bold leading-[1.5] text-primary">
                {t('documents.aiAnalysisResult')}
              </span>
            </div>
            <p className="revy-cjk whitespace-pre-wrap text-xs leading-[1.6]">
              {image.aiDescription}
            </p>
          </div>
          <LoadingButton
            size="sm"
            variant="ghost"
            className="mt-1 self-start"
            loading={analyzing}
            loadingText={t('documents.analyzing')}
            onClick={() => onAnalyze(image.id)}
          >
            <Brain strokeWidth={1.9} />
            <span className="revy-cjk">{t('documents.reanalyze')}</span>
          </LoadingButton>
        </div>
      ) : (
        <LoadingButton
          size="sm"
          variant="outline"
          className="mt-2"
          loading={analyzing}
          loadingText={t('documents.analyzing')}
          onClick={() => onAnalyze(image.id)}
        >
          <Brain strokeWidth={1.9} />
          <span className="revy-cjk">{t('documents.aiAnalysis')}</span>
        </LoadingButton>
      )}
    </div>
  );
};

export default ExtractedImageCard;
